// Hybrid Business Search Service
// Searches BookAPT database first, falls back to Foursquare if no results
// Ensures users ALWAYS see businesses (never a dead end!)

import { foursquarePlacesService, ExternalBusiness } from './foursquarePlacesService';
import { businessInvitationService } from './businessInvitationService';
import SimpleStorage from '../utils/simpleStorage';
import { AuthManager, BusinessOwner } from '../utils/authManager';
import ProductionStorage from '../utils/productionStorage';
import { Platform } from 'react-native';
import { initializeFirebase, getWebDb } from './unifiedAuthService';
import { geocodingService, GeocodingResult } from './geocodingService';

export interface SearchResult {
  businesses: ExternalBusiness[];
  source: 'bookapt' | 'foursquare' | 'hybrid' | 'none';
  message: string;
  shouldShowInviteOption: boolean;
  totalFound: number;
}

class HybridBusinessSearchService {
  private cacheKey = 'hybrid_search_cache';
  private cacheVersion = 'v2'; // Increment when cache key format changes
  private cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Smart search: BookAPT first, then Foursquare, tracks popular businesses
   */
  async search(params: {
    query?: string; // Make query optional since category filter might not need it
    zipcode: string;
    category?: string;
    radius?: number; // in meters
    userLocation?: { lat: number; lng: number };
  }): Promise<SearchResult> {
    try {
      // Normalize query to empty string if undefined to avoid cache key issues
      const normalizedQuery = params.query || '';

      // Step 1: Check cache first (save API calls!)
      // Include category and radius in cache key so different searches don't share cache
      const cached = await this.getCachedResults(normalizedQuery, params.zipcode, params.category, params.radius);
      if (cached) {
        return cached;
      }

      // Step 2: Get user location (geocoding). Same on web and mobile — runs when user opens Explore (dashboard no longer triggers it).
      let userLocation: { lat: number; lng: number } | undefined = params.userLocation;
      if (!userLocation && params.zipcode) {
        const userLoc = await geocodingService.zipcodeToLatLng(params.zipcode);
        if (userLoc) {
          userLocation = { lat: userLoc.lat, lng: userLoc.lng };
        }
      }

      // Step 3: Search BookAPT database first (BookAPT then Foursquare — same logic on web and mobile)
      const bookaptResults = await this.searchBookAPTOnly({
        query: normalizedQuery,
        zipcode: params.zipcode,
        category: params.category,
        radius: params.radius,
        userLocation: userLocation,
      }) || [];

      // If we have good results from BookAPT, use them!
      if (bookaptResults && bookaptResults.length >= 3) {
        const result: SearchResult = {
          businesses: bookaptResults,
          source: 'bookapt',
          message: 'Book instantly with these businesses!',
          shouldShowInviteOption: false,
          totalFound: bookaptResults.length,
        };

        await this.cacheResults(normalizedQuery, params.zipcode, params.category, params.radius, result);
        return result;
      }

      // Step 4: Not enough BookAPT results? Search Foursquare (web and mobile)
      const foursquareResults = await foursquarePlacesService.searchNearby({
        query: normalizedQuery, // Use normalized query (empty string if undefined)
        zipcode: params.zipcode,
        category: params.category, // Pass category to Foursquare
        radius: params.radius, // Pass radius in meters
        limit: 50, // Request up to 50 businesses from Foursquare
      });

      // Merge BookAPT + Foursquare results
      const allBusinesses = [...bookaptResults, ...foursquareResults];

      if (allBusinesses.length === 0) {
        // No results anywhere - prompt to invite
        return {
          businesses: [],
          source: 'none',
          message: `No businesses found for "${params.query}" near ${params.zipcode}. Know one we should add?`,
          shouldShowInviteOption: true,
          totalFound: 0,
        };
      }

      // We have results from Foursquare!
      const result: SearchResult = {
        businesses: allBusinesses,
        source: bookaptResults.length > 0 ? 'hybrid' : 'foursquare',
        message: this.getSearchMessage(bookaptResults.length, foursquareResults.length),
        shouldShowInviteOption: foursquareResults.length > 0, // Show invite for Foursquare results
        totalFound: allBusinesses.length,
      };

      // Cache results
      await this.cacheResults(normalizedQuery, params.zipcode, params.category, params.radius, result);

      // Track popular businesses (for manual outreach)
      await this.trackBusinessInterest(foursquareResults, params.zipcode);

      return result;

    } catch (error) {
      console.error('Error in hybrid search:', error);
      return {
        businesses: [],
        source: 'none',
        message: 'Search temporarily unavailable. Please try again.',
        shouldShowInviteOption: true,
        totalFound: 0,
      };
    }
  }

  /**
   * Calculate distance between two lat/lng points using Haversine formula
   * Returns distance in meters
   */
  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = this.toRadians(lat2 - lat1);
    const dLng = this.toRadians(lng2 - lng1);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Check if query looks like a business name search (vs category/area search)
   */
  private isNameSearch(query: string): boolean {
    if (!query || query.trim().length === 0) {
      return false;
    }
    
    const queryLower = query.toLowerCase().trim();
    
    // Category keywords that indicate category/area search
    const categoryKeywords = ['all', 'hair', 'salon', 'spa', 'restaurant', 'auto', 'repair', 'dentist', 'doctor', 'lawyer', 'halal', 'services'];
    
    // If query is "all" or contains category keywords, it's not a name search
    if (queryLower === 'all' || categoryKeywords.some(keyword => queryLower.includes(keyword))) {
      return false;
    }
    
    // If query is short and doesn't look like a category, treat as name search
    // Business names are usually 2+ words or specific names
    return queryLower.split(/\s+/).length >= 1 && queryLower.length >= 3;
  }

  /**
   * Search BookAPT database only (Firestore + local storage fallback)
   * Public method to search BookAPT businesses separately for instant results
   */
  async searchBookAPTOnly(params: {
    query: string;
    zipcode: string;
    category?: string;
    radius?: number; // in meters
    userLocation?: { lat: number; lng: number };
  }): Promise<ExternalBusiness[]> {
    try {
      console.log('💾 Searching BookAPT database (Firestore + local storage)...', params);
      
      const businesses: ExternalBusiness[] = [];
      
      // Step 1: Search Firestore businesses collection (works across browsers)
      if (Platform.OS === 'web') {
        try {
          const initialized = initializeFirebase();
          const db = getWebDb();
          
          if (initialized && db) {
            const { collection, query, where, getDocs, or } = require('firebase/firestore');
            
            // Name search first: when user types a business name, don't filter by category so we can find any business
            const isNameSearchQuery = this.isNameSearch(params.query);
            
            // Build Firestore query
            let q: any = collection(db, 'businesses');
            const conditions = [];
            
            // Filter by active businesses
            conditions.push(where('isActive', '==', true));
            
            // Filter by category only for category/browse searches — skip for name search so e.g. "Jouni Meat" shows regardless of selected category
            if (params.category && params.category !== 'all' && !isNameSearchQuery) {
              conditions.push(where('category', '==', params.category));
            }
            
            // Apply conditions (isActive + category if specified)
            if (conditions.length > 0) {
              q = query(q, ...conditions);
            }
            
            const querySnapshot = await getDocs(q);
            console.log(`💾 Found ${querySnapshot.size} active businesses in Firestore (before filtering)`);
            
            // Log business names for debugging
            if (querySnapshot.size > 0 && params.query && params.query.trim().length > 0) {
              const businessNames = querySnapshot.docs.slice(0, 10).map(doc => {
                const data = doc.data();
                return data.name || 'No name';
              });
              console.log(`📋 Sample business names in Firestore:`, businessNames);
              console.log(`🔍 Searching for: "${params.query}"`);
            }
            
            // Get user location for distance calculation
            let userLocation: GeocodingResult | null = null;
            if (params.userLocation) {
              userLocation = { lat: params.userLocation.lat, lng: params.userLocation.lng };
            } else if (params.zipcode) {
              // Geocode user zipcode to get lat/lng
              userLocation = await geocodingService.zipcodeToLatLng(params.zipcode);
            }
            
            // For name searches, use larger radius (50 miles = ~80km) or no radius limit
            const searchRadiusMeters = isNameSearchQuery 
              ? (params.radius || 80467) // Default 50 miles for name searches
              : (params.radius || 8047); // Default 5 miles for category searches
            
            // Normalize zipcode for comparison (first 5 digits only) - used as fallback if geocoding fails
            const normalizedZipcode = params.zipcode ? params.zipcode.substring(0, 5) : null;
            
            // Array to store businesses with distance for sorting
            const businessesWithDistance: Array<{ business: ExternalBusiness; distance: number }> = [];
            
            // OPTIMIZATION: Batch geocode all business zipcodes in parallel (much faster!)
            const businessZipcodes = new Set<string>();
            querySnapshot.docs.forEach((doc) => {
              const businessData = doc.data();
              const businessZipcode = businessData.address?.zipcode;
              if (businessZipcode) {
                businessZipcodes.add(businessZipcode);
              }
            });
            
            // Batch geocode all unique zipcodes at once (parallel)
            console.log(`📍 Batch geocoding ${businessZipcodes.size} unique business zipcodes...`);
            const businessLocationsMap = userLocation 
              ? await geocodingService.batchZipcodeToLatLng(Array.from(businessZipcodes))
              : new Map<string, GeocodingResult>();
            console.log(`✅ Geocoded ${businessLocationsMap.size} business locations`);
            
            // Process businesses (now using pre-geocoded locations - no blocking!)
            const businessPromises = querySnapshot.docs.map(async (doc) => {
              const businessData = doc.data();
              
              // Check if matches search query (if provided) - ONLY for name searches, not category/area searches
              // For "all" or category searches, skip name matching and return all businesses within radius/zipcode
              if (params.query && params.query.trim().length > 0 && isNameSearchQuery) {
                const queryLower = params.query.toLowerCase().trim();
                const businessName = businessData.name?.toLowerCase() || '';
                const businessDescription = businessData.description?.toLowerCase() || '';
                const nameMatch = businessName.includes(queryLower);
                const descriptionMatch = businessDescription.includes(queryLower);
                // Also match if every word in the query appears in name or description (e.g. "jouni meat" matches "Jouni Meat Sterling Heights")
                const queryWords = queryLower.split(/\s+/).filter(Boolean);
                const searchableText = `${businessName} ${businessDescription}`;
                const allWordsMatch = queryWords.length > 0 && queryWords.every((word: string) => searchableText.includes(word));
                if (!nameMatch && !descriptionMatch && !allWordsMatch) {
                  return null; // Skip if doesn't match query (only for name searches)
                }
              }
              
              // Calculate distance if user location is available (using pre-geocoded locations)
              let distance = Infinity; // Default to infinity if can't calculate distance
              const businessZipcode = businessData.address?.zipcode;
              
              if (userLocation && businessZipcode) {
                // Use pre-geocoded location (instant lookup, no API call!)
                const businessLocation = businessLocationsMap.get(businessZipcode);
                if (businessLocation) {
                  distance = this.calculateDistance(
                    userLocation.lat,
                    userLocation.lng,
                    businessLocation.lat,
                    businessLocation.lng
                  );
                }
              }
              
              // Filter by radius (if user location is available)
              if (userLocation && distance !== Infinity) {
                if (distance > searchRadiusMeters) {
                  // Business is outside search radius
                  if (!isNameSearchQuery) {
                    // For category searches, skip businesses outside radius
                    return null;
                  }
                  // For name searches, still include but at lower priority (will sort by distance)
                }
              } else {
                // Fallback to zipcode matching if geocoding fails
                if (normalizedZipcode && businessZipcode) {
                  const businessZipNormalized = businessZipcode.toString().substring(0, 5);
                  if (businessZipNormalized !== normalizedZipcode) {
                    // For category searches, require zipcode match if no distance available
                    if (!isNameSearchQuery) {
                      return null; // Skip if zipcode doesn't match
                    }
                    // For name searches, be more lenient - use first 3 digits
                    const userZipPrefix = normalizedZipcode.substring(0, 3);
                    const businessZipPrefix = businessZipNormalized.substring(0, 3);
                    if (userZipPrefix !== businessZipPrefix && !isNameSearchQuery) {
                      return null; // Still skip for category searches if first 3 digits don't match
                    }
                  }
                } else if (!businessZipcode && !isNameSearchQuery) {
                  // If business has no zipcode and it's a category search, skip it
                  return null;
                }
              }
              
              const externalBusiness: ExternalBusiness = {
                id: businessData.id || doc.id,
                name: businessData.name || 'Unknown Business',
                category: businessData.category || 'General',
                address: businessData.address?.street || '',
                city: businessData.address?.city || '',
                state: businessData.address?.state || '',
                zipcode: businessData.address?.zipcode || params.zipcode,
                phone: businessData.phone || '',
                website: businessData.website || '',
                rating: 4.5, // Default rating
                isOnBookAPT: true,
                source: 'bookapt',
                distance: distance !== Infinity ? distance : undefined, // Add distance to business
                ownerId: businessData.ownerId || doc.id, // Set ownerId for BookAPT businesses
                // Business booking policy: when true, customer must pay to book (no pay-later option)
                paymentRequiredAtBooking: !!businessData.paymentRequiredAtBooking,
              };
              
              return { business: externalBusiness, distance };
            });
            
            // Wait for all geocoding operations to complete
            const businessResults = await Promise.all(businessPromises);
            
            // Filter out null results and collect valid businesses
            businessResults.forEach(result => {
              if (result !== null) {
                businessesWithDistance.push(result);
              }
            });
            
            // Sort by distance (closest first), then by name
            businessesWithDistance.sort((a, b) => {
              // If both have distance, sort by distance
              if (a.distance !== Infinity && b.distance !== Infinity) {
                return a.distance - b.distance;
              }
              // If only one has distance, prioritize it
              if (a.distance !== Infinity && b.distance === Infinity) {
                return -1;
              }
              if (a.distance === Infinity && b.distance !== Infinity) {
                return 1;
              }
              // If neither has distance, sort by name
              return a.business.name.localeCompare(b.business.name);
            });
            
            // Extract businesses from sorted array
            const sortedBusinesses = businessesWithDistance.map(item => item.business);
            
            // Add sorted businesses to results
            businesses.push(...sortedBusinesses);
            
            console.log(`📊 Total businesses after filtering: ${businesses.length} (${isNameSearchQuery ? 'name search' : 'category/area search'}, radius: ${(searchRadiusMeters / 1609.34).toFixed(1)} miles)`);
            
            // Debug: Log if we found businesses but they were filtered out
            if (querySnapshot.size > 0 && businesses.length === 0 && params.query) {
              console.warn(`⚠️ Found ${querySnapshot.size} businesses in Firestore but 0 after filtering for query "${params.query}"`);
              console.warn(`   This might be due to name mismatch, distance, or zipcode filtering`);
            }
            
            if (userLocation) {
              console.log(`📍 Using distance-based search from zipcode ${params.zipcode} (${userLocation.lat}, ${userLocation.lng})`);
            } else {
              console.log(`⚠️ Could not geocode zipcode, using zipcode matching as fallback`);
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ Error querying Firestore:', firestoreError);
          console.warn('⚠️  Falling back to local storage search');
        }
      } else {
        // Mobile: Firestore only, zipcode filter in memory — no geocoding (avoids "Could not geocode zipcode" on dashboard/Bookings).
        try {
          const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
          if (initializeFirebase()) {
            const getFirestore = getMobileFirestore();
            if (getFirestore) {
              const firestore = getFirestore();
              const snapshot = await firestore.collection('businesses').where('isActive', '==', true).get();
              const normalizedZip = params.zipcode ? params.zipcode.trim().substring(0, 5) : '';
              const isNameSearchMobile = this.isNameSearch(params.query || '');
              snapshot.docs.forEach((doc: any) => {
                const data = doc.data();
                const bizZip = (data.address?.zipcode || '').trim().substring(0, 5);
                // Same 3-digit zip prefix as web's local radius (neighboring zips in one metro, e.g. 48375 vs 48377)
                if (normalizedZip && bizZip) {
                  if (bizZip.substring(0, 3) !== normalizedZip.substring(0, 3)) return;
                }
                const matchesCategory = !params.category || params.category === 'all' || (data.category || '').toLowerCase() === (params.category || '').toLowerCase();
                if (!isNameSearchMobile && !matchesCategory) return;
                // Match web: category/browse passes Foursquare keywords (e.g. "auto", "hair salon beauty spa") —
                // do not require them to appear in the BookAPT business name (fixes e.g. "Cars of Novi" in Automotive).
                if (isNameSearchMobile && params.query && params.query.trim() && params.query !== 'all') {
                  const queryLower = params.query.toLowerCase().trim();
                  const businessName = (data.name || '').toLowerCase();
                  const businessDescription = (data.description || '').toLowerCase();
                  const nameMatch = businessName.includes(queryLower);
                  const descriptionMatch = businessDescription.includes(queryLower);
                  const queryWords = queryLower.split(/\s+/).filter(Boolean);
                  const searchableText = `${businessName} ${businessDescription}`;
                  const allWordsMatch =
                    queryWords.length > 0 && queryWords.every((word: string) => searchableText.includes(word));
                  if (!nameMatch && !descriptionMatch && !allWordsMatch) return;
                }
                businesses.push({
                  id: doc.id,
                  name: data.name || 'Business',
                  category: data.category || 'General',
                  address: data.address?.street || '',
                  city: data.address?.city || '',
                  state: data.address?.state || '',
                  zipcode: data.address?.zipcode || params.zipcode,
                  phone: data.phone || '',
                  website: data.website || '',
                  rating: typeof data.rating === 'number' ? data.rating : 4.0,
                  isOnBookAPT: true,
                  source: 'bookapt',
                  ownerId: data.ownerId || doc.id,
                } as ExternalBusiness);
              });
            }
          }
        } catch (mobileErr: any) {
          console.warn('⚠️ Mobile Firestore search failed:', mobileErr?.message);
        }
      }

      // Step 2: Fallback to local storage search (for businesses not yet in Firestore)
      
      // Get all business owners from local storage
      // Business owners are stored with key pattern: 'bookapt_business_owner'
      try {
        // Try to get all business owners from storage
        // Since we can't enumerate all keys easily, we'll check for known business owner storage
        const businessOwnerData = await ProductionStorage.getItem('business_owner');
        
        if (businessOwnerData) {
          try {
            const businessOwner: BusinessOwner = JSON.parse(businessOwnerData);
            
            // Check if this business matches the search criteria
            const matchesQuery = !params.query || 
              businessOwner.companyName?.toLowerCase().includes(params.query.toLowerCase()) ||
              businessOwner.name?.toLowerCase().includes(params.query.toLowerCase());
            
            const matchesZipcode = !params.zipcode ||
              businessOwner.address?.zipcode === params.zipcode ||
              businessOwner.address?.zipcode?.substring(0, 5) === params.zipcode.substring(0, 5);
            
            const matchesCategory = !params.category || 
              params.category === 'all' ||
              businessOwner.industry?.toLowerCase().includes(params.category.toLowerCase());
            
            if (matchesQuery && matchesZipcode && matchesCategory) {
              // Skip if already found in Firestore
              if (businesses.find(b => b.id === businessOwner.id)) {
                return;
              }
              
              // Convert BusinessOwner to ExternalBusiness format
              const externalBusiness: ExternalBusiness = {
                id: businessOwner.id,
                name: businessOwner.companyName || businessOwner.name || 'Unknown Business',
                category: businessOwner.industry || 'General',
                address: businessOwner.address?.street || '',
                city: businessOwner.address?.city || '',
                state: businessOwner.address?.state || '',
                zipcode: businessOwner.address?.zipcode || params.zipcode,
                phone: businessOwner.phone || '',
                website: businessOwner.website || '',
                rating: 4.5, // Default rating
                isOnBookAPT: true,
                source: 'bookapt',
                ownerId: businessOwner.id, // Set ownerId for BookAPT businesses
              };
              
              businesses.push(externalBusiness);
              console.log('✅ Found BookAPT business in local storage:', externalBusiness.name);
            }
          } catch (parseError) {
            console.error('Error parsing business owner data:', parseError);
          }
        }
        
        // Also check SimpleStorage for any additional businesses
        // Note: This is limited to the current browser's storage
        if (typeof window !== 'undefined' && window.localStorage) {
          // Check all localStorage keys for business data
          for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (key && key.startsWith('bookapt_business_owner')) {
              try {
                const stored = window.localStorage.getItem(key);
                if (stored) {
                  const businessOwner: BusinessOwner = JSON.parse(stored);
                  
                  // Skip if already added
                  if (businesses.find(b => b.id === businessOwner.id)) {
                    continue;
                  }
                  
                  // Check if this business matches the search criteria
                  const matchesQuery = !params.query || 
                    businessOwner.companyName?.toLowerCase().includes(params.query.toLowerCase()) ||
                    businessOwner.name?.toLowerCase().includes(params.query.toLowerCase());
                  
                  const matchesZipcode = !params.zipcode ||
                    businessOwner.address?.zipcode === params.zipcode ||
                    businessOwner.address?.zipcode?.substring(0, 5) === params.zipcode.substring(0, 5);
                  
                  const matchesCategory = !params.category || 
                    params.category === 'all' ||
                    businessOwner.industry?.toLowerCase().includes(params.category.toLowerCase());
                  
                  if (matchesQuery && matchesZipcode && matchesCategory) {
                    const externalBusiness: ExternalBusiness = {
                      id: businessOwner.id,
                      name: businessOwner.companyName || businessOwner.name || 'Unknown Business',
                      category: businessOwner.industry || 'General',
                      address: businessOwner.address?.street || '',
                      city: businessOwner.address?.city || '',
                      state: businessOwner.address?.state || '',
                      zipcode: businessOwner.address?.zipcode || params.zipcode,
                      phone: businessOwner.phone || '',
                      website: businessOwner.website || '',
                      rating: 4.5,
                      isOnBookAPT: true,
                      source: 'bookapt',
                      ownerId: businessOwner.id, // Set ownerId for BookAPT businesses
                    };
                    
                    businesses.push(externalBusiness);
                    console.log('✅ Found BookAPT business in localStorage:', externalBusiness.name);
                  }
                }
              } catch (error) {
                // Skip invalid entries
                continue;
              }
            }
          }
        }
      } catch (error) {
        console.error('Error searching BookAPT database:', error);
      }
      
      console.log(`💾 Found ${businesses.length} BookAPT business(es) in local storage`);
      return businesses;
    } catch (error) {
      console.error('Error in searchBookAPTOnly:', error);
      return [];
    }
  }

  /**
   * Generate appropriate message based on results
   */
  private getSearchMessage(bookaptCount: number, foursquareCount: number): string {
    if (bookaptCount > 0 && foursquareCount > 0) {
      return `Found ${bookaptCount} on BookAPT + ${foursquareCount} nearby businesses`;
    }
    if (bookaptCount > 0) {
      return `Found ${bookaptCount} businesses on BookAPT!`;
    }
    if (foursquareCount > 0) {
      return `Found ${foursquareCount} nearby businesses (tap to request your appointment)`;
    }
    return 'No businesses found';
  }

  /**
   * Track which businesses users are interested in
   * This helps us know which businesses to personally invite!
   */
  private async trackBusinessInterest(
    businesses: ExternalBusiness[],
    zipcode: string
  ): Promise<void> {
    try {
      const analytics = await this.getAnalytics();
      
      for (const business of businesses) {
        // Track this business was shown to user
        const key = `${business.name}_${zipcode}`;
        analytics[key] = (analytics[key] || 0) + 1;
      }

      await this.saveAnalytics(analytics);
      
      // Check if any business has been shown 10+ times (high interest!)
      for (const [key, count] of Object.entries(analytics)) {
        if (count >= 10) {
          console.log(`🎯 HIGH INTEREST: ${key} shown ${count} times - consider inviting!`);
          // TODO: Alert admin to manually invite this business
        }
      }
    } catch (error) {
      console.error('Error tracking business interest:', error);
    }
  }

  /**
   * When user clicks on a Foursquare business, track their intent
   */
  async trackBusinessClick(business: ExternalBusiness, userId: string): Promise<void> {
    if (business.source === 'foursquare') {
      console.log('👆 User clicked Foursquare business:', business.name);
      
      // Increment click count
      const clicks = await this.getClickAnalytics();
      const key = `${business.name}_${business.zipcode}`;
      clicks[key] = (clicks[key] || 0) + 1;
      await this.saveClickAnalytics(clicks);

      // If 5+ users clicked this business, auto-invite it!
      if (clicks[key] >= 5) {
        console.log(`🚀 AUTO-INVITE: ${business.name} has ${clicks[key]} clicks!`);
        // TODO: Trigger automatic invitation
      }
    }
  }

  /**
   * Cache search results to save API calls
   */
  private async cacheResults(
    query: string,
    zipcode: string,
    category: string | undefined,
    radius: number | undefined,
    result: SearchResult
  ): Promise<void> {
    try {
      const cache = await this.getCache();
      // Include category and radius in cache key so different searches have separate cache entries
      // Use empty string for query if it's empty to ensure consistent cache keys
      const queryPart = query || '';
      const categoryPart = category && category !== 'all' ? `_${category}` : '';
      const radiusPart = radius ? `_r${radius}` : '';
      // Include version in cache key to invalidate old cache entries
      const key = `${this.cacheVersion}_${queryPart}_${zipcode}${categoryPart}${radiusPart}`.toLowerCase();
      
      cache[key] = {
        result,
        timestamp: Date.now(),
      };

      await SimpleStorage.setItem(this.cacheKey, JSON.stringify(cache));
    } catch (error) {
      console.error('Error caching results:', error);
    }
  }

  /**
   * Get cached results if still valid
   */
  private async getCachedResults(
    query: string,
    zipcode: string,
    category: string | undefined,
    radius: number | undefined
  ): Promise<SearchResult | null> {
    try {
      const cache = await this.getCache();
      // Include category and radius in cache key so different searches have separate cache entries
      // Use empty string for query if undefined to ensure consistent cache keys
      const queryPart = query || '';
      const categoryPart = category && category !== 'all' ? `_${category}` : '';
      const radiusPart = radius ? `_r${radius}` : '';
      // Include version in cache key to invalidate old cache entries
      const key = `${this.cacheVersion}_${queryPart}_${zipcode}${categoryPart}${radiusPart}`.toLowerCase();
      console.log('🔑 Cache key:', key);
      console.log('🔍 Cache lookup - query:', queryPart, 'category:', category, 'radius:', radius, 'meters (', radius ? (radius / 1609.34).toFixed(1) : 'N/A', 'miles)');
      const cached = cache[key];
      
      // Log what's actually in the cache for debugging
      const cacheKeys = Object.keys(cache);
      console.log('📦 Cache entries:', cacheKeys.slice(0, 5)); // Show first 5 keys
      
      // Also check for old cache format and log warning
      const oldKey = `${queryPart}_${zipcode}${categoryPart}${radiusPart}`.toLowerCase();
      if (cache[oldKey] && !cached) {
        console.warn('⚠️ Found old cache format, will be ignored. Please clear cache.');
      }

      if (!cached) {
        console.log('❌ No cache match found for key:', key);
        // Check if there are any similar keys (for debugging)
        const similarKeys = cacheKeys.filter(k => k.includes(zipcode));
        if (similarKeys.length > 0) {
          console.log('🔍 Similar cache keys found:', similarKeys);
        }
        return null;
      }
      
      console.log('✅ Cache match found for key:', key);
      // Verify the cached result has the expected category/radius
      if (cached.result && cached.result.businesses && cached.result.businesses.length > 0) {
        const sampleCategories = cached.result.businesses.slice(0, 5).map((b: any) => b.category);
        console.log('📋 Cached businesses sample categories:', sampleCategories);
        
        // Validate name searches: if query exists and doesn't match any business name, invalidate cache
        if (query && query.trim().length > 0) {
          const queryLower = query.toLowerCase().trim();
          const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2); // Get words longer than 2 chars
          
          // Check if ANY business name contains the query (or significant words from query)
          const hasMatchingBusiness = cached.result.businesses.some((b: any) => {
            const businessName = (b.name || '').toLowerCase();
            // Check if business name contains the full query or at least 2 significant words
            if (businessName.includes(queryLower)) {
              return true;
            }
            // Check if business name contains at least 2 words from query (for partial matches)
            if (queryWords.length >= 2) {
              const matchingWords = queryWords.filter(word => businessName.includes(word));
              return matchingWords.length >= 2;
            }
            return false;
          });
          
          if (!hasMatchingBusiness && queryWords.length > 0) {
            console.warn('⚠️ Cache validation failed: Query "' + query + '" not found in cached results');
            console.warn('   Invalidating cache for this key:', key);
            console.warn('   This might be because the business was added after cache was created');
            // Delete the incorrect cache entry
            delete cache[key];
            await SimpleStorage.setItem(this.cacheKey, JSON.stringify(cache));
            return null; // Force a new search
          }
        }
        
        // If we're filtering by category, check if the cached results match
        if (category && category !== 'all') {
          // Define category-specific validation keywords
          const categoryKeywords: { [key: string]: string[] } = {
            'automotive': ['auto', 'car', 'vehicle', 'repair', 'service', 'mechanic', 'tire', 'brake', 'oil change', 'dealership', 'motorcycle'],
            'hair': ['hair', 'salon', 'barber', 'stylist', 'cut'],
            'spa': ['spa', 'massage', 'wellness', 'relaxation'],
            'fitness': ['gym', 'fitness', 'workout', 'training', 'exercise'],
            'restaurant': ['restaurant', 'cafe', 'dining', 'food', 'eatery'],
            'beauty': ['beauty', 'cosmetic', 'makeup', 'skincare'],
          };
          
          const keywords = categoryKeywords[category.toLowerCase()] || [];
          if (keywords.length > 0) {
            // Check if MOST businesses match (at least 50% should match the category)
            const matchingCount = cached.result.businesses.filter((b: any) => {
              if (!b.category) return false;
              const categoryLower = b.category.toLowerCase();
              return keywords.some(keyword => categoryLower.includes(keyword));
            }).length;
            
            const matchPercentage = (matchingCount / cached.result.businesses.length) * 100;
            const hasEnoughMatches = matchPercentage >= 70; // At least 70% should match (stricter threshold)
            
            // Also check sample categories to see if they're clearly wrong
            const sampleMatchCount = sampleCategories.filter((cat: string) => {
              if (!cat) return false;
              const catLower = cat.toLowerCase();
              return keywords.some(keyword => catLower.includes(keyword));
            }).length;
            
            // If less than 60% of sample categories match, likely wrong cache
            const sampleMatchPercentage = (sampleMatchCount / Math.min(5, sampleCategories.length)) * 100;
            const sampleLooksGood = sampleMatchPercentage >= 60;
            
            console.log(`🔍 Cache validation: ${matchingCount}/${cached.result.businesses.length} businesses match (${matchPercentage.toFixed(1)}%)`);
            console.log(`🔍 Sample categories validation: ${sampleMatchCount}/${Math.min(5, sampleCategories.length)} match (${sampleMatchPercentage.toFixed(1)}%)`);
            
            if ((!hasEnoughMatches || !sampleLooksGood) && cached.result.businesses.length > 0) {
              console.warn('⚠️ Cached results do not match category filter!');
              console.warn('   Expected category:', category, 'with keywords:', keywords);
              console.warn('   But found sample categories:', sampleCategories);
              console.warn(`   Only ${matchPercentage.toFixed(1)}% match overall (need at least 70%)`);
              console.warn(`   Only ${sampleMatchPercentage.toFixed(1)}% of sample categories match (need at least 60%)`);
              console.warn('   Invalidating cache for this key:', key);
              // Delete the incorrect cache entry
              delete cache[key];
              await SimpleStorage.setItem(this.cacheKey, JSON.stringify(cache));
              return null; // Force a new search
            }
          }
        }
      }

      // Check if cache is still valid (24 hours)
      const age = Date.now() - cached.timestamp;
      if (age > this.cacheExpiry) {
        return null; // Cache expired
      }

      return cached.result;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get cache from storage
   */
  private async getCache(): Promise<any> {
    try {
      const data = await SimpleStorage.getItem(this.cacheKey);
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }

  /**
   * Get analytics data
   */
  private async getAnalytics(): Promise<{ [key: string]: number }> {
    try {
      const data = await SimpleStorage.getItem('business_interest_analytics');
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }

  /**
   * Save analytics data
   */
  private async saveAnalytics(analytics: { [key: string]: number }): Promise<void> {
    await SimpleStorage.setItem('business_interest_analytics', JSON.stringify(analytics));
  }

  /**
   * Get click analytics
   */
  private async getClickAnalytics(): Promise<{ [key: string]: number }> {
    try {
      const data = await SimpleStorage.getItem('business_click_analytics');
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }

  /**
   * Save click analytics
   */
  private async saveClickAnalytics(clicks: { [key: string]: number }): Promise<void> {
    await SimpleStorage.setItem('business_click_analytics', JSON.stringify(clicks));
  }

  /**
   * Get top requested businesses (for manual outreach)
   */
  async getTopRequestedBusinesses(limit: number = 10): Promise<Array<{ business: string; count: number }>> {
    const analytics = await this.getAnalytics();
    const clicks = await this.getClickAnalytics();

    // Combine impressions and clicks
    const combined: { [key: string]: number } = {};
    
    for (const [key, count] of Object.entries(analytics)) {
      combined[key] = (combined[key] || 0) + count;
    }
    
    for (const [key, count] of Object.entries(clicks)) {
      combined[key] = (combined[key] || 0) + (count * 5); // Clicks worth 5x impressions
    }

    // Sort and return top N
    return Object.entries(combined)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([business, count]) => ({ business, count }));
  }

  /**
   * Clear cache (for testing)
   */
  async clearCache(): Promise<void> {
    await SimpleStorage.removeItem(this.cacheKey);
    console.log('✅ Search cache cleared');
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<{
    totalCached: number;
    oldestEntry: number;
    newestEntry: number;
  }> {
    const cache = await this.getCache();
    const entries = Object.values(cache) as any[];

    if (entries.length === 0) {
      return { totalCached: 0, oldestEntry: 0, newestEntry: 0 };
    }

    const timestamps = entries.map(e => e.timestamp);
    
    return {
      totalCached: entries.length,
      oldestEntry: Math.min(...timestamps),
      newestEntry: Math.max(...timestamps),
    };
  }
}

export const hybridBusinessSearchService = new HybridBusinessSearchService();
export default hybridBusinessSearchService;




